import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { getMe, type OrderSummary } from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';
import { rememberOrder } from '../lib/order-history';
import { rememberTicket } from '../lib/ticket-history';

interface ConfirmLocationState {
  issuedTicketIds?: string[];
  order?: OrderSummary;
}

/**
 * 訂單成立確認頁。
 *
 * - 來源：Checkout 頁 navigate state 帶 `issuedTicketIds` + `order`
 * - 顯示成功 banner + 每張票之 link 至 `/tickets/:id`
 * - 若使用者直接連入本路徑（無 state），顯示退場提示
 */
export function OrderConfirmPage(): JSX.Element {
  const { id: orderId } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useI18n();
  const state = (location.state ?? null) as ConfirmLocationState | null;

  const issued = state?.issuedTicketIds ?? [];

  // 取 user_id 給 ticket-history namespace（防共用裝置撞看）
  const meQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: getMe, retry: false });

  useEffect(() => {
    const userId = meQuery.data?.user_id;
    // 記 order（含 state + total 快照；list 頁不打 BE 取最新）
    if (orderId !== undefined && state?.order !== undefined) {
      rememberOrder(userId, {
        orderId,
        state: state.order.state,
        total: state.order.total,
      });
    } else if (orderId !== undefined) {
      rememberOrder(userId, { orderId });
    }
    // 記 tickets
    for (const tid of issued) {
      rememberTicket(userId, { ticketId: tid, orderId });
    }
  }, [issued, orderId, state?.order, meQuery.data?.user_id]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('confirm.title')}</h1>
        <Link to="/" className="topbar-back">
          {t('common.backHome')}
        </Link>
      </header>

      <section className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <SuccessMark />
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--boat-success)' }}>
            {t('confirm.title')}
          </span>
        </div>

        <div className="kv">
          <span className="kv-label">{t('confirm.orderLabel')}</span>
          <span className="kv-value" style={{ fontFamily: 'monospace' }}>
            {orderId !== undefined ? orderId.slice(0, 8) : '—'}
          </span>
        </div>
        {state?.order && (
          <>
            <div className="kv">
              <span className="kv-label">{t('confirm.statusLabel')}</span>
              <span className="kv-value" style={{ color: 'var(--boat-success)' }}>
                {state.order.state}
              </span>
            </div>
            <div className="kv">
              <span className="kv-label">{t('confirm.paidAmountLabel')}</span>
              <span className="kv-value" style={{ fontSize: 17 }}>
                {formatAmount(state.order.total)}
              </span>
            </div>
          </>
        )}

        {issued.length === 0 ? (
          <p style={{ color: '#777' }}>{t('confirm.noTickets')}</p>
        ) : (
          <>
            <hr className="divider" />
            <h3 style={{ margin: '0 0 8px' }}>
              {t('confirm.issuedHeading', { count: issued.length })}
            </h3>
            {issued.map((tid) => (
              <Link key={tid} to={`/tickets/${tid}`} className="ticket-row">
                <QrGlyph />
                <span style={{ flex: 1 }}>{t('confirm.viewQr', { id: tid.slice(0, 8) })}</span>
                <ChevronRightGlyph />
              </Link>
            ))}
          </>
        )}
      </section>
    </div>
  );
}

/** numeric string → 加 `$` 與千分位（OrderSummary.total 不帶 currency）。 */
function formatAmount(raw: string): string {
  const n = Number(raw);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : raw;
}

/** 綠色圓圈勾 — 訂單成立成功標記。 */
function SuccessMark(): JSX.Element {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1e8e3e"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** QR 小標誌 — 票券列前綴 icon。 */
function QrGlyph(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect
        x="3"
        y="3"
        width="7"
        height="7"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <rect
        x="14"
        y="3"
        width="7"
        height="7"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <rect
        x="3"
        y="14"
        width="7"
        height="7"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <rect x="14" y="14" width="4" height="4" />
      <rect x="20" y="14" width="1" height="1" />
      <rect x="14" y="20" width="1" height="1" />
      <rect x="20" y="20" width="1" height="1" />
    </svg>
  );
}

/** 右向 chevron — 可點列尾標記。 */
function ChevronRightGlyph(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--boat-text-tertiary)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
